import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSocketContext } from '../contexts/SocketContext';
import { useInvitationContext } from '../contexts/InvitationContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface User {
  id: number;
  username: string;
}

interface MainMenuProps {
  onLogout?: () => void;
}

const MainMenu: React.FC<MainMenuProps> = ({ onLogout }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const { socket } = useSocketContext();
  const { pendingInvitation } = useInvitationContext();
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (!socket) return;

    // lobby-created is now handled globally in InvitationContext
    socket.on('invitation-error', (data: { message: string }) => {
      console.error('Invitation error:', data.message);
      alert(data.message);
    });

    return () => {
      socket.off('invitation-error');
    };
  }, [socket]);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API_URL}/auth/me`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.authenticated) {
        setUser(data.user);
      } else {
        navigate('/login');
      }
    } catch (err) {
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      
      // Remove socketToken from localStorage
      localStorage.removeItem('socketToken');
      
      if (onLogout) {
        onLogout();
      }
      navigate('/login');
    } catch (err) {
      // Even if logout fails, clear local data
      localStorage.removeItem('socketToken');
      
      if (onLogout) {
        onLogout();
      }
      navigate('/login');
    }
  };

  const startSoloGame = async (gameType: 'daily' | 'free') => {
    try {
      const response = await fetch(`${API_URL}/games/solo/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ gameType }),
      });
      
      if (response.ok) {
        navigate(`/game?type=solo&mode=${gameType}`);
      } else {
        const data = await response.json();
        alert(data.error || 'Fehler beim Starten des Spiels');
      }
    } catch (err) {
      console.error('Error starting game:', err);
      alert('Verbindungsfehler. Bitte versuche es erneut.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
        <div className="text-white text-xl">Lädt...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 p-2 sm:p-4 overflow-x-hidden">
      <div className="max-w-2xl mx-auto pt-4 sm:pt-8">
        <div className="bg-white rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 mb-4 sm:mb-6 relative overflow-hidden">
          {/* Diagonaler Text-Banner oben rechts */}
          <div className="absolute top-2 sm:top-4 right-[-20px] bg-gradient-to-br from-pink-500 to-red-500 text-white font-bold py-1.5 sm:py-2 px-8 sm:px-12 transform rotate-12 shadow-lg z-10">
            <span className="text-xs sm:text-sm md:text-base whitespace-nowrap">I love you sooo much Freddy ❤️</span>
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-2 text-gray-800 pt-4 sm:pt-0">Frördle Multiplayer</h1>
          <p className="text-center text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">Willkommen, {user?.username}!</p>
          
          {/* Persistent banner is now in App.tsx, add padding if banner is visible */}
          <div className="space-y-3 sm:space-y-4">
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-3 sm:mb-4">Einzelspieler</h2>
            
            <button
              onClick={() => startSoloGame('daily')}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 sm:py-4 px-4 sm:px-6 rounded-lg transition duration-200 text-base sm:text-lg min-h-[44px]"
            >
              Tägliches Frördle
            </button>
            
            <button
              onClick={() => startSoloGame('free')}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 sm:py-4 px-4 sm:px-6 rounded-lg transition duration-200 text-base sm:text-lg min-h-[44px]"
            >
              Frei Frördlen
            </button>
            
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mt-6 sm:mt-8 mb-2 sm:mb-3">Multiplayer</h2>
            <p className="text-sm sm:text-base text-gray-600 mb-2">
              Spiele gemeinsam oder gegeneinander. Wähle einen Modus und lade deine Freunde ein.
            </p>
            
            <div className="bg-purple-50 rounded-xl p-3 sm:p-4 space-y-2 sm:space-y-3">
              <button
                onClick={() => navigate('/multiplayer?mode=alternating')}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 sm:py-4 px-4 sm:px-6 rounded-lg transition duration-200 text-base sm:text-lg min-h-[44px]"
              >
                Gemeinsames Frördle
              </button>
              
              <button
                onClick={() => navigate('/multiplayer?mode=race')}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-3 sm:py-4 px-4 sm:px-6 rounded-lg transition duration-200 text-base sm:text-lg min-h-[44px]"
              >
                1 vs 1 Frördle
              </button>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mt-6 sm:mt-8">
              <button
                onClick={() => navigate('/friends')}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-4 sm:px-6 rounded-lg transition duration-200 text-sm sm:text-base min-h-[44px]"
              >
                Freunde
              </button>
              
              <button
                onClick={() => navigate('/settings')}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-4 sm:px-6 rounded-lg transition duration-200 text-sm sm:text-base min-h-[44px]"
              >
                Einstellungen
              </button>
              
              <button
                onClick={() => navigate('/stats')}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-4 sm:px-6 rounded-lg transition duration-200 text-sm sm:text-base min-h-[44px]"
              >
                Statistiken
              </button>
              
              <button
                onClick={handleLogout}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-4 sm:px-6 rounded-lg transition duration-200 text-sm sm:text-base min-h-[44px]"
              >
                Abmelden
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MainMenu;

